import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usePageTitle from "@/hooks/use-page-title";
import { Briefcase, Palette, Cog, Truck, HeartHandshake, Send, Users, ShieldCheck, Sparkles, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { briefcase: Briefcase, palette: Palette, cog: Cog, truck: Truck,
  handshake: HeartHandshake, send: Send, users: Users, shield: ShieldCheck, sparkles: Sparkles };

export default function Careers() {
  usePageTitle("Careers");
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);  // id of the currently expanded job (accordion)
  useEffect(() => {
    fetch(`${API}/careers?active_only=true`)
      .then((r) => r.json())
      .then((rows) => setOpenings(Array.isArray(rows) ? rows : []))
      .catch(() => setOpenings([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="overline mb-3">Join our team</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter">Build your career at <span className="text-[#FF6B00]">Brothers</span></h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl mx-auto">
            We're a fast-growing printing &amp; signage studio in Navi Mumbai looking for craftspeople who love turning ideas into things you can touch, walk past and photograph. Ready to grow with us?
          </p>
          <a href="mailto:print@brothersdigital.in?subject=Career%20application"
            className="mt-8 inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3">
            <Send size={14} /> Apply Now
          </a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="mb-10">
          <div className="overline mb-2">Current Openings</div>
          <h2 className="font-display text-4xl font-black tracking-tighter">
            {loading ? "Loading openings…" : (openings.length ? `We're hiring for ${openings.length} role${openings.length > 1 ? "s" : ""}` : "New roles opening soon")}
          </h2>
        </div>
        {!loading && openings.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 p-10 text-center bg-white">
            <div className="font-semibold text-zinc-700">No active vacancies right now.</div>
            <p className="text-sm text-zinc-500 mt-2">We're always growing though — send us your CV and we'll get in touch when the right role opens up.</p>
            <a href="mailto:print@brothersdigital.in?subject=Open%20application"
              className="mt-5 inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold px-5 py-2.5 text-sm">
              <Send size={14} /> Send Open Application
            </a>
          </div>
        )}
        {openings.length > 0 && (
          <div className="space-y-3">
            {openings.map((o) => {
              const Icon = ICONS[o.icon] || Briefcase;
              const isOpen = expanded === o.id;
              return (
                <div
                  key={o.id}
                  className={`rounded-2xl border-2 bg-white transition-all ${isOpen ? "border-[#FF6B00] shadow-lg" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid={`career-card-${o.id}`}
                >
                  {/* Collapsed header row */}
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : o.id)}
                    className="w-full flex items-center gap-4 p-4 text-left"
                    data-testid={`career-toggle-${o.id}`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isOpen ? "bg-[#FF6B00] text-white" : "bg-[#FF6B00]/10 text-[#FF6B00]"}`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-black truncate">{o.title}</div>
                      <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5">
                        <span className="uppercase tracking-wider font-bold">{o.employment_type || "Full-time"}</span>
                        {o.location && <><span>·</span><span>{o.location}</span></>}
                        {(o.skills || []).length > 0 && <><span>·</span><span className="truncate">{o.skills.slice(0, 3).join(" · ")}</span></>}
                      </div>
                    </div>
                    <ChevronDown size={18} className={`text-zinc-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180 text-[#FF6B00]" : ""}`} />
                  </button>

                  {/* Expanded body */}
                  {isOpen && (
                    <div className="px-4 pb-5 pt-1 border-t border-zinc-100" data-testid={`career-body-${o.id}`}>
                      <div className="mt-4 grid md:grid-cols-3 gap-6">
                        <div className="md:col-span-2 space-y-4">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Role</div>
                            <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed">{o.description || "—"}</p>
                          </div>
                          {(o.skills || []).length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Required Skills</div>
                              <div className="flex flex-wrap gap-1.5">
                                {o.skills.map((s) => (
                                  <span key={s} className="text-[11px] font-semibold border border-zinc-300 rounded-full px-2.5 py-1 bg-zinc-50">{s}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="space-y-3">
                          <div className="rounded-xl bg-orange-50 border border-orange-100 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-bold">Apply</div>
                            <p className="text-xs text-zinc-700 mt-1 mb-3">Send your CV + a short note about why you'd be a great fit.</p>
                            <a
                              href={`mailto:${o.apply_email || "print@brothersdigital.in"}?subject=Application%20for%20${encodeURIComponent(o.title)}`}
                              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#e65a00] text-white font-bold rounded-full px-4 py-2 text-sm"
                              data-testid={`career-apply-${o.id}`}
                            >
                              Apply for this role <Send size={12} />
                            </a>
                          </div>
                          {o.employment_type && (
                            <div className="text-[11px] text-zinc-500">
                              <b>Type:</b> {o.employment_type}
                              {o.location && <><br/><b>Location:</b> {o.location}</>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00] font-bold mb-4">Don't see your role?</div>
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-4">Send us your CV anyway.</h2>
          <p className="text-zinc-300 mb-6">We're always looking for talented, driven people who take pride in their craft.</p>
          <a href="mailto:print@brothersdigital.in?subject=Open%20application"
            className="inline-flex items-center gap-2 bg-white text-black font-bold px-6 py-3">
            <Send size={14} /> Email your CV
          </a>
          <p className="text-xs text-zinc-500 mt-6">
            Or drop by our office in Taloja Phase 2, Navi Mumbai · <Link to="/contact" className="text-[#FF6B00] font-semibold hover:underline">Get directions</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
